import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, Users, MapPin, TrendingUp, Tag, DollarSign, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { useEventStats, TIMEFRAMES } from "@/hooks/useEventStats";
import { useAdminStatsGeneration } from "@/hooks/useAdminStatsGeneration";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const CHART_COLORS = [
  "hsl(var(--secondary))",
  "hsl(var(--primary))",
  "hsl(var(--info))",
  "hsl(var(--warning))",
  "hsl(var(--destructive))",
];

export const EventStatsCard = () => {
  const { timeframe, setTimeframe, stats, chartData, isLoading, timeframeLabel, generatedAt, isRefreshing } = useEventStats();
  const { generateStats, isGenerating } = useAdminStatsGeneration();

  const hasNoCache = !isLoading && !stats;

  // Auto-generate stats on first load if no cache exists
  useEffect(() => {
    if (hasNoCache && !isGenerating("event")) {
      generateStats("event");
    }
  }, [hasNoCache]);

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-secondary" />
              Event Stats
            </CardTitle>
            <CardDescription className="text-xs">
              {timeframeLabel}
              {generatedAt && (
                <span className="ml-2 text-muted-foreground">
                  · Updated {formatDistanceToNow(new Date(generatedAt))} ago
                  {isRefreshing && <span className="ml-1 text-xs opacity-70">(refreshing...)</span>}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => generateStats("event")}
              disabled={isGenerating("event") || isRefreshing}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isGenerating("event") || isRefreshing) && "animate-spin")} />
            </Button>
            <div className="flex gap-0.5">
              {TIMEFRAMES.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={timeframe === value ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("h-6 px-2 text-xs", timeframe === value && "font-bold")}
                  onClick={() => setTimeframe(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : hasNoCache ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground mb-4">No cached stats available</p>
            <Button onClick={() => generateStats("event")} disabled={isGenerating("event")}>
              {isGenerating("event") ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Stats"
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Stats Grid - Inline compact */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Total:</span>
                <span className="font-semibold">{stats?.total ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-success" />
                <span className="text-muted-foreground">Active:</span>
                <span className="font-semibold">{stats?.active ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-info" />
                <span className="text-muted-foreground">Upcoming:</span>
                <span className="font-semibold">{stats?.upcoming ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Booked:</span>
                <span className="font-semibold">{stats?.totalBooked ?? 0} / {stats?.totalCapacity ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Fill Rate:</span>
                <span className="font-semibold">{stats?.fillRate ?? 0}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Revenue:</span>
                <span className="font-semibold">£{stats?.totalRevenue?.toFixed(2) ?? '0.00'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Venues:</span>
                <span className="font-semibold">{stats?.uniqueVenues ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Tags:</span>
                <span className="font-semibold">{stats?.uniqueTags ?? 0}</span>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Events Over Time */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Events</h4>
                <div className="h-28">
                  {chartData.eventsOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.eventsOverTime} margin={{ left: 0, right: 4 }}>
                        <XAxis
                          dataKey="date"
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          interval="preserveStartEnd"
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                        />
                        <YAxis
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                          width={24}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                        />
                        <Bar dataKey="events" fill="hsl(var(--secondary))" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

              {/* Attendees Over Time */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Attendees</h4>
                <div className="h-28">
                  {chartData.eventsOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.eventsOverTime} margin={{ left: 0, right: 4 }}>
                        <XAxis
                          dataKey="date"
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          interval="preserveStartEnd"
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                        />
                        <YAxis
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                          width={24}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                        />
                        <Bar dataKey="attendees" fill="hsl(var(--info))" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

              {/* Top Venues */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Top Venues</h4>
                <div className="h-28">
                  {chartData.venueBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.venueBreakdown}
                          dataKey="count"
                          nameKey="venue"
                          cx="30%"
                          cy="50%"
                          outerRadius={35}
                        >
                          {chartData.venueBreakdown.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                          wrapperStyle={{ fontSize: "10px", paddingLeft: "8px" }}
                          formatter={(value, entry: any) => {
                            const shortVenue = value.length > 12 ? value.substring(0, 12) + "..." : value;
                            return `${shortVenue}: ${entry.payload.count}`;
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
